import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Circle,
  Square,
  Play,
  Pause,
  Download,
  Share2,
  Send,
  RefreshCw,
  Camera,
  Mail,
  MessageSquare,
  Phone,
  CheckCircle,
  Loader2,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecordVideo() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [sendConfig, setSendConfig] = useState({
    method: "email",
    recipients: "",
    subject: "Personal video message for you",
    message: ""
  });

  const timerRef = useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      toast.error("Could not access camera/microphone");
      console.error(error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const toggleCamera = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCameraOn(videoTrack.enabled);
      }
    }
  };

  const toggleMic = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicOn(audioTrack.enabled);
      }
    }
  };

  const startRecording = () => {
    if (!stream) {
      toast.error("Camera not available");
      return;
    }

    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9'
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
    setIsRecording(true);
    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  };

  const resetRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  };

  const downloadRecording = () => {
    if (recordedUrl) {
      const a = document.createElement('a');
      a.href = recordedUrl;
      a.download = `sellecast-recording-${Date.now()}.webm`;
      a.click();
      toast.success("Video downloaded!");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSend = async () => {
    if (!sendConfig.recipients) {
      toast.error("Enter recipient(s)");
      return;
    }

    setSending(true);
    
    // Simulate sending
    await new Promise(r => setTimeout(r, 2000));
    
    toast.success(`Video sent via ${sendConfig.method}!`);
    setSending(false);
    setShowSendModal(false);
  };

  const saveToLibrary = async () => {
    if (!recordedBlob) return;

    try {
      // Create project
      const response = await axios.post(`${API}/projects`, {
        title: `Recorded Video - ${new Date().toLocaleDateString()}`,
        description: "Personal recorded video",
        video_type: "sales",
        target_duration: recordingTime
      });

      toast.success("Saved to library!");
      navigate("/library");
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Record Video</span>
          <Button 
            variant="outline"
            className="border-border"
            onClick={() => navigate("/library")}
          >
            Library
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Camera Preview */}
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
            {recordedUrl && !isRecording ? (
              <video
                src={recordedUrl}
                className="w-full h-full object-contain"
                controls
                autoPlay
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`w-full h-full object-cover ${!cameraOn ? 'opacity-0' : ''}`}
              />
            )}

            {/* Camera off overlay */}
            {!cameraOn && !recordedUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-background-subtle">
                <div className="text-center">
                  <VideoOff className="w-16 h-16 text-text-muted mx-auto mb-2" />
                  <p className="text-text-muted">Camera off</p>
                </div>
              </div>
            )}

            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 px-3 py-2 rounded-lg">
                <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-accent-warning' : 'bg-accent-error animate-pulse'}`} />
                <span className="text-white font-mono">{formatTime(recordingTime)}</span>
                {isPaused && <span className="text-accent-warning text-sm">Paused</span>}
              </div>
            )}

            {/* Camera/Mic controls overlay */}
            {!recordedUrl && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`w-12 h-12 rounded-full ${cameraOn ? 'bg-white/20 hover:bg-white/30' : 'bg-accent-error/80 hover:bg-accent-error'}`}
                  onClick={toggleCamera}
                >
                  {cameraOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`w-12 h-12 rounded-full ${micOn ? 'bg-white/20 hover:bg-white/30' : 'bg-accent-error/80 hover:bg-accent-error'}`}
                  onClick={toggleMic}
                >
                  {micOn ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
                </Button>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            {!recordedUrl ? (
              <>
                {!isRecording ? (
                  <Button
                    size="lg"
                    className="bg-accent-error hover:bg-accent-error/90 text-white px-8 py-6"
                    onClick={startRecording}
                  >
                    <Circle className="w-5 h-5 mr-2 fill-white" />
                    Start Recording
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-border px-6 py-6"
                      onClick={pauseRecording}
                    >
                      {isPaused ? <Play className="w-5 h-5 mr-2" /> : <Pause className="w-5 h-5 mr-2" />}
                      {isPaused ? "Resume" : "Pause"}
                    </Button>
                    <Button
                      size="lg"
                      className="bg-accent-error hover:bg-accent-error/90 text-white px-8 py-6"
                      onClick={stopRecording}
                    >
                      <Square className="w-5 h-5 mr-2 fill-white" />
                      Stop Recording
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border px-6 py-6"
                  onClick={resetRecording}
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Record Again
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border px-6 py-6"
                  onClick={downloadRecording}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border px-6 py-6"
                  onClick={saveToLibrary}
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Save to Library
                </Button>
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary-hover text-white px-8 py-6"
                  onClick={() => setShowSendModal(true)}
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send to Client
                </Button>
              </>
            )}
          </div>

          {/* Tips */}
          {!isRecording && !recordedUrl && (
            <Card className="bg-background-paper border-border">
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-text-primary mb-2">Recording Tips</h3>
                <ul className="text-sm text-text-muted space-y-1">
                  <li>• Good lighting on your face</li>
                  <li>• Quiet environment for clear audio</li>
                  <li>• Look at the camera, not the screen</li>
                  <li>• Keep it concise - 30-60 seconds works best</li>
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Send Modal */}
      {showSendModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowSendModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-background-paper rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-text-primary">Send Video</h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowSendModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Send method */}
              <div>
                <Label className="text-text-primary text-sm mb-2 block">Send via</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "email", label: "Email", icon: <Mail className="w-4 h-4" /> },
                    { id: "sms", label: "SMS", icon: <Phone className="w-4 h-4" /> },
                    { id: "whatsapp", label: "WhatsApp", icon: <MessageSquare className="w-4 h-4" /> }
                  ].map((method) => (
                    <Card
                      key={method.id}
                      onClick={() => setSendConfig({...sendConfig, method: method.id})}
                      className={`cursor-pointer transition-all ${
                        sendConfig.method === method.id
                          ? 'bg-primary/10 border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <CardContent className="p-3 text-center">
                        <div className="text-primary mx-auto mb-1">{method.icon}</div>
                        <div className="text-xs text-text-primary">{method.label}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <Label className="text-text-primary text-sm mb-2 block">
                  {sendConfig.method === "email" ? "Email Address(es)" : "Phone Number(s)"}
                </Label>
                <Input
                  value={sendConfig.recipients}
                  onChange={(e) => setSendConfig({...sendConfig, recipients: e.target.value})}
                  placeholder={sendConfig.method === "email" ? "client@example.com" : "+1234567890"}
                  className="bg-background border-border text-text-primary"
                />
              </div>

              {/* Subject (email only) */}
              {sendConfig.method === "email" && (
                <div>
                  <Label className="text-text-primary text-sm mb-2 block">Subject</Label>
                  <Input
                    value={sendConfig.subject}
                    onChange={(e) => setSendConfig({...sendConfig, subject: e.target.value})}
                    className="bg-background border-border text-text-primary"
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <Label className="text-text-primary text-sm mb-2 block">Message (optional)</Label>
                <Textarea
                  value={sendConfig.message}
                  onChange={(e) => setSendConfig({...sendConfig, message: e.target.value})}
                  placeholder="Add a personal message..."
                  className="bg-background border-border text-text-primary"
                  rows={3}
                />
              </div>

              <Button
                onClick={handleSend}
                disabled={sending}
                className="w-full bg-primary hover:bg-primary-hover text-white py-5"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Video</>
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
